import React, { useEffect, useState } from "react";
import { Wallet as WalletIcon, ArrowDownLeft, ArrowUpRight, IndianRupee, ShieldCheck, Sparkles } from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/layout/Layout";
import AuthGuard from "../components/AuthGuard";
import EmptyState from "../components/EmptyState";
import { api } from "../api/client";
import { useAuth } from "../contexts/AuthContext";

const inr = (n) => `₹${Number(n || 0).toFixed(2)}`;

const REASON_LABEL = {
    refund: "Refund",
    referral_bonus: "Referral bonus",
    promo_credit: "Promo credit",
    manual_adjustment: "Manual adjustment",
    order_payment: "Order payment",
    subscription_payment: "Subscription payment",
};

const TxnRow = ({ t }) => {
    const credit = t.type === "credit";
    return (
        <li
            className="flex items-start gap-3 py-4"
            data-testid={`wallet-txn-${t.id}`}
        >
            <span
                className={`mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-full ring-1 ${
                    credit
                        ? "bg-emerald-50 text-emerald-600 ring-emerald-100"
                        : "bg-rose-50 text-rose-600 ring-rose-100"
                }`}
            >
                {credit ? <ArrowDownLeft className="h-4 w-4" /> : <ArrowUpRight className="h-4 w-4" />}
            </span>
            <div className="min-w-0 flex-1">
                <p className="line-clamp-1 text-sm font-medium text-zinc-900">{t.description}</p>
                <p className="mt-0.5 text-[11px] uppercase tracking-wide text-zinc-400">
                    {REASON_LABEL[t.reason] || t.reason} • {new Date(t.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                </p>
            </div>
            <div className="text-right">
                <p className={`text-sm font-semibold ${credit ? "text-emerald-700" : "text-zinc-900"}`}>
                    {credit ? "+" : "−"}{inr(t.amount)}
                </p>
                <p className="text-[11px] text-zinc-400">Bal {inr(t.balance_after)}</p>
            </div>
        </li>
    );
};

const AdminCreditForm = ({ onCredited }) => {
    const [email, setEmail] = useState("");
    const [amount, setAmount] = useState("");
    const [reason, setReason] = useState("promo_credit");
    const [description, setDescription] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        const amt = parseFloat(amount);
        if (!email || !(amt > 0) || !description.trim()) {
            toast.error("Email, amount > 0 and description are required");
            return;
        }
        setSubmitting(true);
        try {
            // Resolve user id: backend credit expects user_id. We do a quick lookup via admin lookup endpoint if available;
            // fall back: ask admin to paste an existing user_id in email field is bad UX. We expose a dedicated find endpoint
            // — but here we just call /wallet/credit with the assumption admin pastes the user_id. To make this friendly we
            // accept email and look the user up via a backend lookup. Since we don't have an admin lookup yet, we send the
            // raw value as user_id when there is no @.
            let userIdToCredit = email;
            if (email.includes("@")) {
                // No admin user-lookup endpoint built in Phase 5. Inform admin.
                toast.error("Paste the customer's user_id (not email). User lookup will land in Phase 7 admin OS.");
                setSubmitting(false);
                return;
            }
            await api.post("/wallet/credit", {
                user_id: userIdToCredit,
                amount: amt,
                reason,
                description,
            });
            toast.success(`Credited ${inr(amt)} to ${userIdToCredit.slice(0, 8)}…`);
            setEmail("");
            setAmount("");
            setDescription("");
            onCredited?.();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const msg = typeof detail === "string" ? detail : detail?.message || "Credit failed";
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form
            onSubmit={submit}
            className="rounded-3xl border border-brand-100 bg-gradient-to-br from-white to-brand-50/40 p-6 shadow-soft"
            data-testid="wallet-admin-credit-form"
        >
            <div className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-brand-700" />
                <p className="label-uppercase text-brand-700">Admin tools</p>
            </div>
            <h3 className="mt-1 text-lg font-semibold tracking-tight text-zinc-900">Credit a customer's wallet</h3>
            <p className="mt-1 text-xs text-zinc-500">Refunds, promo credits or manual adjustments.</p>

            <div className="mt-5 grid gap-3 sm:grid-cols-2">
                <input
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Customer user_id"
                    className="input-clean sm:col-span-2"
                    data-testid="wallet-admin-userid-input"
                />
                <div className="relative">
                    <IndianRupee className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
                    <input
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        placeholder="Amount"
                        type="number"
                        min="1"
                        step="0.01"
                        className="input-clean pl-10"
                        data-testid="wallet-admin-amount-input"
                    />
                </div>
                <select
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    className="input-clean"
                    data-testid="wallet-admin-reason-select"
                >
                    <option value="promo_credit">Promo credit</option>
                    <option value="refund">Refund</option>
                    <option value="referral_bonus">Referral bonus</option>
                    <option value="manual_adjustment">Manual adjustment</option>
                </select>
                <input
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Description (visible to user)"
                    className="input-clean sm:col-span-2"
                    data-testid="wallet-admin-description-input"
                />
            </div>
            <button
                type="submit"
                disabled={submitting}
                className="btn-brand mt-5 w-full sm:w-auto"
                data-testid="wallet-admin-credit-btn"
            >
                {submitting ? "Crediting..." : "Credit wallet"}
            </button>
        </form>
    );
};

const WalletContent = () => {
    const { user } = useAuth();
    const [wallet, setWallet] = useState(null);
    const [txns, setTxns] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        try {
            setLoading(true);
            const [{ data: w }, { data: tx }] = await Promise.all([
                api.get("/wallet"),
                api.get("/wallet/transactions", { params: { page: 1, page_size: 50 } }),
            ]);
            setWallet(w);
            setTxns(tx.items || []);
        } catch (e) {
            // unauthenticated path already handled by AuthGuard
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    if (loading) {
        return (
            <div className="space-y-4">
                <div className="h-48 skeleton rounded-3xl" />
                <div className="h-64 skeleton rounded-3xl" />
            </div>
        );
    }

    return (
        <div className="grid gap-8 lg:grid-cols-[1fr_360px]" data-testid="wallet-page">
            <div className="space-y-6">
                <section
                    className="relative overflow-hidden rounded-3xl border border-brand-100 bg-gradient-to-br from-brand-700 via-brand-500 to-brand-300 p-8 text-white shadow-brand"
                    data-testid="wallet-balance-card"
                >
                    <div className="absolute -right-10 -top-10 h-44 w-44 rounded-full bg-white/10 blur-3xl" />
                    <div className="absolute -left-12 -bottom-12 h-48 w-48 rounded-full bg-white/10 blur-3xl" />
                    <div className="relative">
                        <p className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.2em] text-white/80">
                            <WalletIcon className="h-3.5 w-3.5" /> PILLS Wallet
                        </p>
                        <p className="mt-4 text-sm text-white/80">Available balance</p>
                        <p className="mt-1 text-5xl font-semibold tracking-tight" data-testid="wallet-balance-amount">
                            {inr(wallet?.balance || 0)}
                        </p>
                        <p className="mt-4 max-w-md text-xs text-white/80">
                            Apply at checkout to instantly reduce your bill. Refunds for failed payments are credited here automatically.
                        </p>
                        <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-[11px] font-medium backdrop-blur-sm">
                            <Sparkles className="h-3 w-3" /> Coverage applied at checkout
                        </div>
                    </div>
                </section>

                <section className="rounded-3xl border border-zinc-100 bg-white p-6 shadow-soft" data-testid="wallet-transactions">
                    <div className="flex items-end justify-between">
                        <div>
                            <p className="label-uppercase">Activity</p>
                            <h2 className="mt-1 text-xl font-semibold tracking-tight text-zinc-900">Recent transactions</h2>
                        </div>
                        <p className="text-xs text-zinc-500">{txns.length} entries</p>
                    </div>
                    {txns.length === 0 ? (
                        <div className="mt-6">
                            <EmptyState
                                title="No transactions yet"
                                description="When you receive refunds, promo credits or apply your wallet at checkout, you'll see it here."
                            />
                        </div>
                    ) : (
                        <ul className="mt-4 divide-y divide-zinc-100">
                            {txns.map((t) => <TxnRow key={t.id} t={t} />)}
                        </ul>
                    )}
                </section>
            </div>

            <aside className="space-y-6">
                <div className="rounded-3xl border border-zinc-100 bg-white p-6 shadow-soft" data-testid="wallet-side-card">
                    <p className="label-uppercase">How it works</p>
                    <ul className="mt-3 space-y-3 text-sm text-zinc-600">
                        <li className="flex gap-3"><span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand-50 text-[11px] font-bold text-brand-700">1</span>Receive credits from refunds, referrals or promos.</li>
                        <li className="flex gap-3"><span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand-50 text-[11px] font-bold text-brand-700">2</span>Toggle <span className="font-medium text-brand-700">"Use PILLS Wallet"</span> at checkout.</li>
                        <li className="flex gap-3"><span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand-50 text-[11px] font-bold text-brand-700">3</span>If your wallet covers the full total, payment bypasses Zoho instantly.</li>
                    </ul>
                </div>

                {user?.role === "admin" && (
                    <AdminCreditForm onCredited={load} />
                )}
            </aside>
        </div>
    );
};

const Wallet = () => (
    <Layout>
        <section className="mx-auto max-w-7xl px-6 py-10 lg:px-12">
            <header className="mb-8">
                <p className="label-uppercase">Account</p>
                <h1 className="mt-1 text-3xl font-semibold tracking-tight text-zinc-900 sm:text-4xl">Wallet</h1>
                <p className="mt-2 max-w-2xl text-sm text-zinc-500">Your PILLS Wallet — for refunds, referral rewards, and instant payment at checkout.</p>
            </header>
            <AuthGuard>
                <WalletContent />
            </AuthGuard>
        </section>
    </Layout>
);

export default Wallet;
